import {
  CheckCircle,
  ChevronRight,
  Mail,
  Printer,
  Trash2,
  PrinterCheck,
  BellRing,
  Ban,
  ShieldCheck,
  Loader2,
} from "lucide-react";
import { BellIcon, EyeIcon, RefreshCWIcon } from "../../components/icons";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CfLoader from "../../common/CfLoader/CfLoader";
import DraftsRevokeModal from "../../common/DraftsRevokeModal/DraftsRevokeModal";
import CredentialRevokeModal from "../../common/CredentialRevokeModal/CredentialRevokeModal";
import ProgressBar from "../../common/ProgressBar/ProgressBar";
import Toast from "../../common/Toast/Toast";
import ToolTip from "../../common/ToolTip/ToolTip";
import EntityConstants from "../../configs/EntityConstants";
import PendingIcon from "../../resources/icons/alert-triangle.svg";
import { ReactComponent as FlyingMail } from "../../resources/icons/flying-mail.svg";
import podcastIcon from "../../resources/icons/podcast-icon.svg";
import CorrectIcon from "../../resources/icons/succuss-tick.svg";
import { GET, POST } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import useNavigationStore from "../../store/navigationStore";
import MultiSectionModal from "../../common/MultiSectionModal/MultiSectionModal";
import VerifyCredentialsModal from "../../common/VerifyCredentialsModal/VerifyCredentialsModal";
import QaTestedBadge from "../../common/QaTestedBadge/QaTestedBadge";
import CertificatePreviewModal from "../NotifyRecipients/CertificatePreviewModal";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import { useTranslation } from "react-i18next";
import { handleRovingTabKeyDown } from "../../utils/keyboardNavigation";
import {
  hasQaPollingInProgress,
  isVerificationQrType,
  resolveShouldQaPoll,
} from "../../utils/postIssuanceQaPolling";

const ISSUANCE_STATUS_POLLING_INTERVAL = 2000;
const RECIPIENTS_POLLING_INTERVAL = 5000;

const normalizeQaStatus = (status) =>
  typeof status === "string" ? status.trim().toLowerCase() : "";

const isQaVerified = (status) => normalizeQaStatus(status) === "verified";

const isQaFailed = (status) => normalizeQaStatus(status) === "failed";

const isQaPollingInProgress = (status) =>
  normalizeQaStatus(status) === "in-progress";

const mergeRecipientQaFields = (recipient, update) => {
  if (!update) return recipient;

  return {
    ...recipient,
    ...(update.qa_status !== undefined ? { qa_status: update.qa_status } : {}),
  };
};

const getRecipientStatusFilter = (recipientTab) => {
  switch (recipientTab) {
    case "issued":
      return "Issued,Printed,Notified";
    case "failed":
      return "Failed";
    case "revoked":
      return "Revoked";
    case "printed":
      return "Printed,Printed_and_Notified";
    default:
      return null;
  }
};

const buildRecipientSnapshot = (recipient) => ({
  credential_id: recipient.credential_id,
  recipient_name: recipient.recipient_name,
  recipient_email: recipient.recipient_email,
  roll_number: recipient.roll_number,
  notified_at: recipient.notified_at,
  qa_status: recipient.qa_status,
});

const RECIPIENT_TABLE_HIDDEN_KEYS = [
  "credential_id",
  "credential_digital_preview",
  "credential_print_preview",
];

const getRecipientTableHiddenKeys = (showQaVerificationColumn) =>
  showQaVerificationColumn
    ? RECIPIENT_TABLE_HIDDEN_KEYS
    : [...RECIPIENT_TABLE_HIDDEN_KEYS, "qa_status"];

const fetchPendingIssuanceTransactions = async ({
  issuerCredentialTemplateId,
  limit = 10,
  offset = 0,
}) => {
  if (!issuerCredentialTemplateId)
    return { error: "Missing issuerCredentialTemplateId" };

  try {
    const response = await GET(
      `${EntityConstants.GET_PENDING_ISSUANCE}?issuer_template_id=${issuerCredentialTemplateId}&limit=${limit}&offset=${offset}`,
    );
    const data = await response.json();

    if (data.error) {
      return { error: data.error };
    }

    return {
      results: data.data.results,
      next: data.data.next,
      previous: data.data.previous,
    };
  } catch (error) {
    console.error("Error fetching transactions:", error);
    return { error: "Failed to fetch transactions" };
  }
};

export default function LegacyPendingIssuancePage() {
  const { credentialTemplateId, issuer_credential_template_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [
    isPostIssuanceVerificationEnabled,
    setIsPostIssuanceVerificationEnabled,
  ] = useState(authContext.isPostIssuanceVerificationEnabled === true);

  useEffect(() => {
    let cancelled = false;

    const syncPostIssuanceVerificationPermission = async () => {
      const permissions = await authContext.syncUserPermissions();
      if (cancelled) return;

      setIsPostIssuanceVerificationEnabled(
        permissions?.is_post_issuance_verification_enabled === true,
      );
    };

    syncPostIssuanceVerificationPermission();

    return () => {
      cancelled = true;
    };
  }, [authContext.syncUserPermissions]);

  useEffect(() => {
    setIsPostIssuanceVerificationEnabled(
      authContext.isPostIssuanceVerificationEnabled === true,
    );
  }, [authContext.isPostIssuanceVerificationEnabled]);

  const [certificateName, setCertificateName] = useState("");
  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );

  useEffect(() => {
    if (!issuer_credential_template_id) {
      setCertificateName("");
      return;
    }

    setCertificateName("");
    const controller = new AbortController();
    const fetchName = async () => {
      try {
        const res = await GET(
          `${EntityConstants.GET_USER_PREVIEW_SVG}?issuer_credential_template_id=${issuer_credential_template_id}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        setCertificateName(data.certificate_name || "");
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.debug("Failed to fetch certificate name fallback:", error);
        }
      }
    };

    fetchName();
    return () => controller.abort();
  }, [issuer_credential_template_id]);
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const [transactions, setTransactions] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecipientsLoading, setIsRecipientsLoading] = useState(true);
  const [recipientsIssuedCount, setRecipientsIssuedCount] = useState(0);
  const [recipientsNotifiedCount, setRecipientsNotifiedCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [recipientsOffset, setRecipientsOffset] = useState(0);
  const limit = 10;
  const [previous, setPrevious] = useState(null);
  const [next, setNext] = useState(null);
  const [recipientsPrevious, setRecipientsPrevious] = useState(null);
  const [recipientsNext, setRecipientsNext] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const templateType = queryParams.get("templateType");
  const [isCertificateModalOpen, setIsCertificateModalOpen] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState("");
  const [selectedPrintUrl, setSelectedPrintUrl] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [selectedRecipientsMap, setSelectedRecipientsMap] = useState({});
  const [isAllSelectedViaHeader, setIsAllSelectedViaHeader] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyingRowMap, setVerifyingRowMap] = useState({});
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [isVerifyAllConfirmOpen, setIsVerifyAllConfirmOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [
    isDeletePendingIssuanceModalOpen,
    setIsDeletePendingIssuanceModalOpen,
  ] = useState(false);
  const [isDeleteRecipientsModalOpen, setIsDeleteRecipientsModalOpen] =
    useState(false);
  const [pendingIssuanceToDelete, setPendingIssuanceToDelete] = useState(null);
  const [rowLoadingMap, setRowLoadingMap] = useState({});
  const [recipientToRevoke, setRecipientToRevoke] = useState(null);
  const navigationState = location.state || {};
  const [isPolling, setIsPolling] = useState(() =>
    Boolean(navigationState.transaction_id),
  );
  const [isQaPolling, setIsQaPolling] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const pollingIntervals = useRef({});
  const recipientsPollingInterval = useRef(null);
  const hasShownIssuanceResultToastRef = useRef(false);
  const [activeRecipientTab, setActiveRecipientTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const searchTermRef = useRef("");
  const [qrType, setQrType] = useState(null);

  const showQaVerificationColumn = isPostIssuanceVerificationEnabled;

  const isVerificationEnabled =
    isPostIssuanceVerificationEnabled && isVerificationQrType(qrType);

  useEffect(() => {
    searchTermRef.current = searchTerm || "";
  }, [searchTerm]);

  useEffect(() => {
    hasShownIssuanceResultToastRef.current = false;
    setIsPolling(Boolean(navigationState.transaction_id));
  }, [navigationState.transaction_id]);

  const syncQaPollingFromRecipients = (recipientList, nextQrType = qrType) => {
    setIsQaPolling(
      resolveShouldQaPoll({
        isPostIssuanceVerificationEnabled,
        qrType: nextQrType,
        recipients: recipientList,
      }),
    );
  };

  // Keep QA polling in sync when permission/QR/recipients change outside a fetch.
  useEffect(() => {
    setIsQaPolling(
      resolveShouldQaPoll({
        isPostIssuanceVerificationEnabled,
        qrType,
        recipients,
      }),
    );
  }, [isPostIssuanceVerificationEnabled, qrType, recipients]);

  const performSearchAPI = async (
    searchTerm,
    signal,
    offset = recipientsOffset,
  ) => {
    try {
      if (!issuer_credential_template_id) {
        setRecipients([]);
        return;
      }

      const transactionId = navigationState.transaction_id;
      let url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${offset}&search=${searchTerm}`;

      if (activeRecipientTab === "issued") {
        url += "&status=Issued,Printed,Notified";
      } else if (activeRecipientTab === "failed") {
        url += "&status=Failed";
      } else if (activeRecipientTab === "revoked") {
        url += "&status=Revoked";
      } else if (activeRecipientTab === "printed") {
        url += "&status=Printed,Printed_and_Notified";
      }

      if (transactionId) {
        url += `&transaction_id=${transactionId}`;
      }

      const response = await GET(url, { signal });
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        setRecipients([]);
      } else {
        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);
        const nextQrType = data.qr_type !== undefined ? data.qr_type : qrType;
        if (data.qr_type !== undefined) {
          setQrType(data.qr_type);
        }
        syncQaPollingFromRecipients(data.results, nextQrType);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error during search:", error);
        setRecipients([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const handleSearchInputChange = (e) => {
    const term = e.target.value ? e.target.value : "";
    setSearchTerm(term);

    if (term === "") {
      setRecipientsOffset(0);
      fetchRecipients("", false);
      return;
    }

    if (!issuer_credential_template_id) {
      setRecipients([]);
      return;
    }

    setRecipientsOffset(0);

    handleSearch(term, {
      onStart: () => {
        setIsRecipientsLoading(true);
      },
      onComplete: () => setIsRecipientsLoading(false),
      onError: () => {
        setRecipients([]);
        setIsRecipientsLoading(false);
      },
      minChars: 1,
    });
  };

  const isDeletionDisabled = useMemo(() => {
    if (selectedRecipientIds.length === 0) {
      return true;
    }
    const selected = recipients.filter((r) =>
      selectedRecipientIds.includes(r.credential_id),
    );
    const allowedStatuses = ["Issued", "Failed"];

    return selected.some((r) => !allowedStatuses.includes(r.status));
  }, [recipients, selectedRecipientIds]);

  const isRevocationDisabled = useMemo(() => {
    if (selectedRecipientIds.length !== 1) return true;

    const selectedRecipient = recipients.find((r) =>
      selectedRecipientIds.includes(r.credential_id),
    );

    if (!selectedRecipient) return true;

    const eligibleStatuses = [
      "Issued",
      "Notified",
      "Printed",
      "Printed_and_Notified",
    ];
    return !eligibleStatuses.includes(selectedRecipient.status);
  }, [recipients, selectedRecipientIds]);

  const showRevokeButton = useMemo(() => {
    if (selectedRecipientIds.length === 0) return false;
    const hiddenStatuses = ["Revoked", "Failed"];
    const selected = recipients.filter((r) =>
      selectedRecipientIds.includes(r.credential_id),
    );

    if (selected.length === 0) return false;

    const allHidden = selected.every((r) => hiddenStatuses.includes(r.status));
    if (allHidden) return false;

    return true;
  }, [selectedRecipientIds, recipients]);

  const isAllCurrentPageSelected = useMemo(
    () =>
      recipients.length > 0 &&
      recipients.every((recipient) =>
        selectedRecipientIds.includes(recipient.credential_id),
      ),
    [recipients, selectedRecipientIds],
  );

  const isPrintNotifyDisabled = recipientsIssuedCount === 0;
  const isIssueDisabled = templateType !== "Legacy" && isPolling;

  const verifiableSelected = useMemo(
    () =>
      selectedRecipientIds
        .map((credentialId) => selectedRecipientsMap[credentialId])
        .filter(
          (recipient) =>
            recipient &&
            !isQaVerified(recipient.qa_status) &&
            !isQaPollingInProgress(recipient.qa_status),
        ),
    [selectedRecipientIds, selectedRecipientsMap],
  );

  const hasReverifySelection = useMemo(
    () =>
      verifiableSelected.some((recipient) => isQaFailed(recipient.qa_status)),
    [verifiableSelected],
  );

  const clearRecipientSelection = () => {
    setSelectedRecipientIds([]);
    setSelectedRecipientsMap({});
    setIsAllSelectedViaHeader(false);
  };

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      if (location.state?.programme && location.state?.batch) {
        useNavigationStore.setState({
          currentProgramme: location.state.programme,
          currentBatch: location.state.batch,
        });
      } else {
        navigate("/issuer/workspace", { replace: true });
      }
    }
  }, [currentProgramme, currentBatch, navigate, location.state]);

  useEffect(() => {
    if (!Array.isArray(transactions) || transactions.length === 0 || isLoading)
      return;

    transactions.forEach((txn) => {
      if (
        txn.status === "In Progress" &&
        !pollingIntervals.current[txn.legacy_transaction_id]
      ) {
        setRowLoadingMap((prev) => ({
          ...prev,
          [txn.legacy_transaction_id]: true,
        }));

        pollingIntervals.current[txn.legacy_transaction_id] = setInterval(
          async () => {
            try {
              const res = await GET(
                `${EntityConstants.GET_ISSUANCE_STATUS}?legacy_transaction_id=${txn.legacy_transaction_id}`,
              );
              const data = await res.json();

              setTransactions((prevTxns) =>
                prevTxns.map((t) =>
                  t.legacy_transaction_id === txn.legacy_transaction_id
                    ? { ...t, issuance_status: data }
                    : t,
                ),
              );

              if (
                data.transaction_status === "Failed" ||
                data.total_recipients - data.issued - data.failed <= 0
              ) {
                clearInterval(
                  pollingIntervals.current[txn.legacy_transaction_id],
                );
                delete pollingIntervals.current[txn.legacy_transaction_id];

                if (data.transaction_status === "Issued") {
                  Toast({
                    type: "success",
                    title: t("workspace:success"),
                    message: t("workspace:issuance_successfully_completed"),
                    persistent: true,
                  });
                } else if (data.transaction_status === "Failed") {
                  Toast({
                    type: "error",
                    title: t("workspace:issuance_failed"),
                    message: t("workspace:issuance_failed_description"),
                    persistent: true,
                  });
                } else {
                  Toast({
                    type: "error",
                    title: t("workspace:issuance_failed"),
                    message: t("workspace:something_went_wrong"),
                    persistent: true,
                  });
                }

                if (data.transaction_status !== "Failed") {
                  await Promise.all([
                    fetchTransactions(true),
                    fetchRecipients(),
                    fetchRecipientStats(),
                  ]);
                }

                setRowLoadingMap((prev) => ({
                  ...prev,
                  [txn.legacy_transaction_id]: false,
                }));
              }
            } catch (error) {
              Toast({
                type: "error",
                title: t("workspace:error"),
                message:
                  error?.message ||
                  t("workspace:failed_fetch_transactions_status"),
              });
            }
          },
          ISSUANCE_STATUS_POLLING_INTERVAL,
        );
      }
    });

    Object.keys(pollingIntervals.current).forEach((id) => {
      const txn = transactions.find(
        (t) => t.legacy_transaction_id === id && t.status === "In Progress",
      );
      if (!txn) {
        clearInterval(pollingIntervals.current[id]);
        delete pollingIntervals.current[id];
        setRowLoadingMap((prev) => ({
          ...prev,
          [id]: false,
        }));
      }
    });

    return () => {
      Object.values(pollingIntervals.current).forEach(clearInterval);
      pollingIntervals.current = {};
    };
  }, [isLoading, transactions]);

  useEffect(() => {
    if (!templateType) {
      const fallbackTemplateType =
        localStorage.getItem("templateType") || "Legacy";

      const newParams = new URLSearchParams(location.search);

      newParams.set("templateType", fallbackTemplateType);

      navigate(`${location.pathname}?${newParams.toString()}`, {
        replace: true,
      });

      if (fallbackTemplateType === "Legacy") {
        fetchTransactions();
      }
    } else if (templateType === "Legacy") {
      fetchTransactions();
    }
  }, [templateType, issuer_credential_template_id, offset, location.pathname]);

  useEffect(() => {
    if (issuer_credential_template_id) {
      if (!searchTerm) {
        fetchRecipients("", false);
      }
    }
  }, [issuer_credential_template_id, recipientsOffset, activeRecipientTab]);

  useEffect(() => {
    setQrType(null);
  }, [issuer_credential_template_id]);

  useEffect(() => {
    if (issuer_credential_template_id) {
      fetchRecipientStats();
    }
  }, [issuer_credential_template_id]);

  const handleDeletePendingIssuance = (transactionId) => {
    setPendingIssuanceToDelete(transactionId);
    setIsDeletePendingIssuanceModalOpen(true);
  };

  const handleConfirmDeletePendingIssuance = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await POST(EntityConstants.DISCARD_PENDING_ISSUANCE, {
        legacy_transaction_id: pendingIssuanceToDelete,
      });

      if (response.ok) {
        Toast({
          type: "success",
          title: t("workspace:success"),
          message: t("workspace:successfully_pending_issuance_deleted"),
        });
        await fetchTransactions();
      } else {
        const data = await response.json();
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error || t("workspace:failed_pending_issuance_deleted"),
        });
      }
    } catch (error) {
      console.error("Error deleting pending issuance:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_pending_issuance_deleted"),
      });
    } finally {
      setIsDeleteLoading(false);
      setPendingIssuanceToDelete(null);
      setIsDeletePendingIssuanceModalOpen(false);
    }
  };

  const handleDeleteRecipients = () => {
    if (selectedRecipientIds.length === 0) {
      Toast({
        type: "error",
        title: t("workspace:no_recipients_selected"),
        message: t("workspace:select_recipient_description"),
      });
      return;
    }

    const selectedRecipients = recipients.filter((r) =>
      selectedRecipientIds.includes(r.credential_id),
    );

    const allowedStatuses = ["Issued", "Failed", "Revoked"];
    const allAreAllowed = selectedRecipients.every((r) =>
      allowedStatuses.includes(r.status),
    );

    if (allAreAllowed) {
      setIsDeleteRecipientsModalOpen(true);
    }
  };

  const handleConfirmDeleteRecipients = async () => {
    setIsDeleteLoading(true);
    try {
      const response = await POST(EntityConstants.DELETE_RECIPIENTS, {
        credential_ids: selectedRecipientIds,
      });

      const data = await response.json();

      if (response.ok) {
        if (
          data.message &&
          data.message.includes("0 credential(s) deleted") &&
          data.skipped_details &&
          data.skipped_details.length > 0
        ) {
          Toast({
            type: "error",
            title: t("workspace:cannot_delete"),
            message: t("workspace:delete_certificate_description"),
            persistent: true,
          });
        } else if (data.deleted_ids && data.deleted_ids.length > 0) {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message: t("workspace:success_recipients_deleted"),
          });
        } else {
          Toast({
            type: "warning",
            title: t("workspace:no_action"),
            message: t("workspace:no_action_description"),
          });
        }

        await fetchRecipientStats();
        await fetchRecipients();
        clearRecipientSelection();
      } else {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error || t("workspace:failed_delete_recipients"),
        });
      }
    } catch (error) {
      console.error("Error deleting recipients:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_delete_recipients"),
      });
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteRecipientsModalOpen(false);
    }
  };

  const handleRevokeRecipient = () => {
    if (selectedRecipientIds.length === 1) {
      setRecipientToRevoke(selectedRecipientIds[0]);
    }
  };

  const fetchRecipientStats = async ({
    showErrorToast = true,
    updateLoading = true,
  } = {}) => {
    if (!issuer_credential_template_id) return;
    try {
      const response = await GET(
        `${EntityConstants.GET_RECIPIENT_STATS}/${issuer_credential_template_id}`,
      );
      const data = await response.json();
      if (data.error) {
        if (showErrorToast) {
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.error,
          });
        }
      } else {
        setRecipientsIssuedCount(data.data.total_recipient);
        setRecipientsNotifiedCount(data.data.notified_recipient);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      if (showErrorToast) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_transactions"),
        });
      }
    } finally {
      if (updateLoading) {
        setIsLoading(false);
      }
    }
  };

  const fetchTransactions = async (silent = false) => {
    if (!silent) setIsLoading(true);
    const result = await fetchPendingIssuanceTransactions({
      issuerCredentialTemplateId: issuer_credential_template_id,
      limit,
      offset,
    });

    if (result.error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: result.error,
      });
    } else {
      setTransactions(result.results);
      setNext(result.next);
      setPrevious(result.previous);
    }

    if (!silent) setIsLoading(false);
  };

  const fetchRecipients = async (searchedTerm, isManual = false) => {
    if (!issuer_credential_template_id) return;
    if (isManual || recipients.length === 0) {
      setIsRecipientsLoading(true);
    }
    if (isManual) {
      setIsManualRefresh(true);
    }
    const transactionId = navigationState.transaction_id;
    let url;
    if (searchedTerm) {
      url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}&search=${searchedTerm}`;
    } else {
      url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}`;
    }

    const statusFilter = getRecipientStatusFilter(activeRecipientTab);
    if (statusFilter) {
      url += `&status=${statusFilter}`;
    }

    if (transactionId) {
      url += `&transaction_id=${transactionId}`;
    }
    try {
      const response = await GET(url);
      const data = await response.json();
      if (data.error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error,
        });
      } else {
        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);
        const nextQrType = data.qr_type !== undefined ? data.qr_type : qrType;
        if (data.qr_type !== undefined) {
          setQrType(data.qr_type);
        }
        syncQaPollingFromRecipients(data.results, nextQrType);

        if (data.transaction_status === "In Progress") {
          setIsPolling(true);
        } else {
          setIsPolling(false);
          localStorage.removeItem("transaction_id");
        }
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_recipients"),
      });
    } finally {
      setIsRecipientsLoading(false);
      setIsManualRefresh(false);
    }
  };

  const handleManualRefresh = () => {
    fetchRecipients(null, true);
    fetchRecipientStats();
  };

  useEffect(() => {
    if (!issuer_credential_template_id) return;
    if (!isPolling && !isQaPolling) return;

    let cancelled = false;

    const pollRecipients = async () => {
      try {
        const transactionId = navigationState.transaction_id;
        const activeSearchTerm = searchTermRef.current;
        let url = `${EntityConstants.GET_RECIPIENTS}/${issuer_credential_template_id}?limit=${limit}&offset=${recipientsOffset}`;

        if (activeSearchTerm) {
          url += `&search=${activeSearchTerm}`;
        }

        const statusFilter = getRecipientStatusFilter(activeRecipientTab);
        if (statusFilter) {
          url += `&status=${statusFilter}`;
        }

        if (transactionId) {
          url += `&transaction_id=${transactionId}`;
        }

        const response = await GET(url);
        const data = await response.json();

        if (cancelled) return;

        if (data.error) {
          Toast({
            type: "error",
            title: t("workspace:error"),
            message: data.error,
          });
          return;
        }

        setRecipients(data.results);
        setRecipientsPrevious(data.previous);
        setRecipientsNext(data.next);

        const nextQrType = data.qr_type !== undefined ? data.qr_type : qrType;
        if (data.qr_type !== undefined) {
          setQrType(data.qr_type);
        }

        const resultsById = Object.fromEntries(
          (data.results || []).map((recipient) => [
            recipient.credential_id,
            recipient,
          ]),
        );
        setSelectedRecipientsMap((prev) => {
          if (Object.keys(prev).length === 0) return prev;

          const updated = { ...prev };
          Object.keys(updated).forEach((credentialId) => {
            const result = resultsById[credentialId];
            if (result) {
              updated[credentialId] = buildRecipientSnapshot(result);
            }
          });
          return updated;
        });

        const transactionStillInProgress =
          data.transaction_status === "In Progress";
        const qaStillInProgress = resolveShouldQaPoll({
          isPostIssuanceVerificationEnabled,
          qrType: nextQrType,
          recipients: data.results,
        });

        setIsQaPolling(qaStillInProgress);

        await fetchRecipientStats({
          showErrorToast: !transactionStillInProgress && !qaStillInProgress,
          updateLoading: false,
        });

        if (cancelled) return;

        if (!transactionStillInProgress && isPolling) {
          setIsPolling(false);
          localStorage.removeItem("transaction_id");

          if (!hasShownIssuanceResultToastRef.current) {
            hasShownIssuanceResultToastRef.current = true;

            if (data.transaction_status === "Completed") {
              Toast({
                type: "success",
                title: t("workspace:success"),
                message: t("workspace:issuance_successfully_completed"),
                persistent: true,
              });
            } else if (data.transaction_status === "Failed") {
              Toast({
                type: "error",
                title: t("workspace:issuance_failed"),
                message: t("workspace:issuance_failed_description"),
                persistent: true,
              });
            } else {
              Toast({
                type: "error",
                title: t("workspace:error"),
                message: t("workspace:something_went_wrong"),
              });
            }
          }
        }

        if (!transactionStillInProgress && !qaStillInProgress) {
          clearInterval(recipientsPollingInterval.current);
          recipientsPollingInterval.current = null;
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Error polling recipients:", error);
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:failed_fetch_recipients"),
        });
      }
    };

    if (recipientsPollingInterval.current === null) {
      pollRecipients();
      recipientsPollingInterval.current = setInterval(
        pollRecipients,
        RECIPIENTS_POLLING_INTERVAL,
      );
    }

    return () => {
      cancelled = true;
      if (recipientsPollingInterval.current) {
        clearInterval(recipientsPollingInterval.current);
        recipientsPollingInterval.current = null;
      }
    };
  }, [
    issuer_credential_template_id,
    recipientsOffset,
    isPolling,
    isQaPolling,
    isPostIssuanceVerificationEnabled,
    qrType,
    activeRecipientTab,
    // Without this the interval keeps polling the transaction id captured when
    // the effect last ran, rather than the current one.
    navigationState.transaction_id,
  ]);

  const handleSelectAllRecipients = (checked) => {
    if (checked) {
      const currentPageRecipients = recipients.map(buildRecipientSnapshot);
      const currentPageIds = currentPageRecipients.map(
        (recipient) => recipient.credential_id,
      );

      setSelectedRecipientIds((prev) => [
        ...new Set([...prev, ...currentPageIds]),
      ]);
      setSelectedRecipientsMap((prev) => ({
        ...prev,
        ...Object.fromEntries(
          currentPageRecipients.map((recipient) => [
            recipient.credential_id,
            recipient,
          ]),
        ),
      }));
      setIsAllSelectedViaHeader(true);
    } else {
      const currentPageIds = new Set(
        recipients.map((recipient) => recipient.credential_id),
      );

      setSelectedRecipientIds((prev) =>
        prev.filter((id) => !currentPageIds.has(id)),
      );
      setSelectedRecipientsMap((prev) => {
        const updated = { ...prev };
        currentPageIds.forEach((id) => {
          delete updated[id];
        });
        return updated;
      });
      setIsAllSelectedViaHeader(false);
    }
  };

  const handleSelectRecipient = (credentialId, checked) => {
    if (checked) {
      const recipient = recipients.find(
        (r) => r.credential_id === credentialId,
      );
      if (recipient) {
        setSelectedRecipientsMap((prev) => ({
          ...prev,
          [credentialId]: buildRecipientSnapshot(recipient),
        }));
      }
      setSelectedRecipientIds((prev) =>
        prev.includes(credentialId) ? prev : [...prev, credentialId],
      );
      setIsAllSelectedViaHeader(false);
    } else {
      setSelectedRecipientIds((prev) =>
        prev.filter((id) => id !== credentialId),
      );
      setSelectedRecipientsMap((prev) => {
        const updated = { ...prev };
        delete updated[credentialId];
        return updated;
      });
      setIsAllSelectedViaHeader(false);
    }
  };

  const getVerifyResponseMessage = (data, verified) =>
    data?.message ||
    data?.errors ||
    data?.error ||
    (verified
      ? t("workspace:verify_success_default")
      : t("workspace:verify_failed_default"));

  const postVerifyCredentials = async (payload) => {
    const response = await POST(
      EntityConstants.POST_VERIFY_POST_ISSUANCE,
      payload,
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(getVerifyResponseMessage(data, false));
    }

    return data;
  };

  const showVerifyErrorToast = (error) => {
    Toast({
      type: "error",
      title: t("workspace:error"),
      message:
        error?.message ||
        (typeof error === "string"
          ? error
          : t("workspace:verify_failed_default")),
    });
  };

  const applyVerifyResults = async (data, { clearSelection = true } = {}) => {
    Toast({
      type: data.verified ? "success" : "error",
      title: data.verified ? t("workspace:success") : t("workspace:error"),
      message: getVerifyResponseMessage(data, data.verified),
    });

    const results = Array.isArray(data.results) ? data.results : [];

    if (results.length > 0) {
      const resultsMap = Object.fromEntries(
        results.map((result) => [result.credential_id, result]),
      );

      setRecipients((prev) =>
        prev.map((recipient) =>
          mergeRecipientQaFields(
            recipient,
            resultsMap[recipient.credential_id],
          ),
        ),
      );

      setSelectedRecipientsMap((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((credentialId) => {
          const result = resultsMap[credentialId];
          if (!result) return;
          updated[credentialId] = mergeRecipientQaFields(
            updated[credentialId],
            result,
          );
        });
        return updated;
      });

      if (isVerificationEnabled && hasQaPollingInProgress(results)) {
        setIsQaPolling(true);
      }
    }

    const inProgressIds = results
      .filter((result) => isQaPollingInProgress(result.qa_status))
      .map((result) => result.credential_id)
      .filter(Boolean);

    await fetchRecipients(searchTerm || "", false);

    if (isVerificationEnabled && inProgressIds.length > 0) {
      setRecipients((prev) =>
        prev.map((recipient) => {
          if (!inProgressIds.includes(recipient.credential_id)) {
            return recipient;
          }
          if (
            isQaVerified(recipient.qa_status) ||
            isQaFailed(recipient.qa_status)
          ) {
            return recipient;
          }
          return { ...recipient, qa_status: "In-Progress" };
        }),
      );
      setIsQaPolling(true);
    }

    if (clearSelection) {
      clearRecipientSelection();
    }
  };

  const handleVerify = async () => {
    if (!isVerificationEnabled) return;

    if (isAllSelectedViaHeader) {
      setIsVerifyAllConfirmOpen(true);
      return;
    }

    if (verifiableSelected.length === 0) {
      Toast({
        type: "warning",
        title: t("workspace:no_selection"),
        message: t("workspace:verify_no_eligible_selection"),
      });
      return;
    }

    setIsVerifyModalOpen(true);
  };

  const handleConfirmVerifyAll = async () => {
    if (!isVerificationEnabled || isVerifying) return;

    setIsVerifying(true);
    try {
      const data = await postVerifyCredentials({
        transaction_id: issuer_credential_template_id,
      });
      await applyVerifyResults(data);
      setIsVerifyAllConfirmOpen(false);
    } catch (error) {
      console.error("Error verifying certificates:", error);
      showVerifyErrorToast(error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleConfirmVerify = async (credentialIds) => {
    if (!isVerificationEnabled) return;

    const idsToVerify = Array.isArray(credentialIds)
      ? credentialIds
      : verifiableSelected.map((recipient) => recipient.credential_id);

    if (idsToVerify.length === 0) {
      Toast({
        type: "warning",
        title: t("workspace:no_selection"),
        message: t("workspace:verify_no_eligible_selection"),
      });
      return;
    }

    setIsVerifying(true);
    try {
      const data = await postVerifyCredentials({
        credential_ids: idsToVerify,
      });
      await applyVerifyResults(data);
      setIsVerifyModalOpen(false);
    } catch (error) {
      console.error("Error verifying certificates:", error);
      showVerifyErrorToast(error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRowVerify = async (credentialId) => {
    if (!isVerificationEnabled) return;

    setVerifyingRowMap((prev) => ({ ...prev, [credentialId]: true }));
    try {
      const data = await postVerifyCredentials({
        credential_ids: [credentialId],
      });
      await applyVerifyResults(data, { clearSelection: false });
    } catch (error) {
      console.error("Error verifying certificate:", error);
      showVerifyErrorToast(error);
    } finally {
      setVerifyingRowMap((prev) => ({ ...prev, [credentialId]: false }));
    }
  };

  const getNextStepText = (currentStep) => {
    switch (currentStep) {
      case "ZIP Uploaded":
        return t("workspace:upload_recipients_data");
      case "Validated":
      case "Issue":
        return t("workspace:issuance_summary");
      default:
        return currentStep;
    }
  };

  const getResumeUrl = (transaction) => {
    if (transaction.status === "ZIP Uploaded") {
      return {
        pathname: `/issuer/workspace/batches/legacy-issuance/${credentialTemplateId}/${transaction.issuer_credential_template_id}/${encodeURIComponent(certificateName)}/${templateType}`,
        state: {
          transactionData: transaction,
          fromResume: true,
        },
      };
    } else if (
      transaction.status === "Validated" ||
      transaction.status === "Issue"
    ) {
      return `/issuer/workspace/batches/legacy-summary/${transaction.legacy_transaction_id}/${transaction.issuer_credential_template_id}`;
    }

    return `/issuer/workspace/batches/legacy-issuance/${credentialTemplateId}/${transaction.legacy_transaction_id}/${encodeURIComponent(certificateName)}/${templateType}`;
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };
  const handleWorkspaceClick = () => {
    navigate("/issuer/workspace");
  };

  const handlePdfPrint = () => {
    if (isPrintNotifyDisabled) return;

    localStorage.setItem(
      "issuerCredentialTemplateId",
      issuer_credential_template_id,
    );
    localStorage.setItem("credentialTemplateId", credentialTemplateId);
    navigate(`/issuer/credentials/${issuer_credential_template_id}/print`, {
      state: { issuerCredentialTemplateId: issuer_credential_template_id },
    });
  };

  const handleNotifyRecipients = () => {
    if (isPrintNotifyDisabled) return;

    localStorage.setItem(
      "issuerCredentialTemplateId",
      issuer_credential_template_id,
    );
    localStorage.setItem("credentialTemplateId", credentialTemplateId);
    localStorage.setItem("templateType", templateType);
    navigate(
      `/issuer/credentials/${credentialTemplateId}/${issuer_credential_template_id}/notify`,
    );
  };

  const handlePreview = useCallback(
    (e, previewUrl, printPreviewUrl, recipient) => {
      e.stopPropagation();
      setIsCertificateModalOpen(true);
      setIsLoadingPreview(false);
      setSelectedPreviewUrl(previewUrl);
      setSelectedPrintUrl(printPreviewUrl);
      setSelectedRecipient(recipient);
    },
    [],
  );

  const handleIssueNavigate = () => {
    if (isIssueDisabled) return;

    if (templateType === "Legacy") {
      navigate(
        `/issuer/workspace/batches/legacy-issuance/${credentialTemplateId}/${issuer_credential_template_id}/${encodeURIComponent(certificateName)}/${templateType}`,
      );
    } else {
      if (!currentProgramme?.programme_code) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: t("workspace:something_went_wrong"),
        });
        return;
      }
      navigate(
        `/issuer/certificate/issue/${credentialTemplateId}/${issuer_credential_template_id}/${currentProgramme?.programme_code}`,
      );
    }
  };

  const formatHeaderText = (key) => {
    if (key === "qa_status") {
      return t("workspace:verification").toUpperCase();
    }

    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toUpperCase();
  };

  const handleTabChange = (tab) => {
    setActiveRecipientTab(tab);
    setRecipientsOffset(0);
    clearRecipientSelection();
    setSearchTerm("");
  };

  const recipientTabs = ["all", "issued", "failed", "revoked", "printed"];

  const handleRecipientTabKeyDown = (event, tab) => {
    handleRovingTabKeyDown({
      event,
      items: recipientTabs,
      currentItem: tab,
      onChange: handleTabChange,
      getElementId: (nextTab) => `legacy-pending-issuance-tab-${nextTab}`,
    });
  };

  if (!currentProgramme || !currentBatch) {
    return (
      <div
        className="w-full mt-20 bg-cfSecondary min-h-screen flex justify-center items-center"
        id={pageRootTestIds.legacyPendingIssuance}
      >
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-20 bg-cfSecondary w-full">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col lg:flex-row items-start lg:items-center min-h-[4rem] justify-between w-full`}
        >
          <div className="min-w-0 w-full">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-500 truncate min-w-0 max-w-full">
                  <button
                    id="legacy-pending-issuance-breadcrumb-workspace"
                    onClick={handleWorkspaceClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleWorkspaceClick();
                      }
                    }}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {t("workspace:workspace")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <button
                    id="legacy-pending-issuance-breadcrumb-programme"
                    onClick={handleProgrammeClick}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleProgrammeClick();
                      }
                    }}
                    tabIndex={0}
                    title={
                      currentProgramme.programme_name ||
                      t("workspace:programme")
                    }
                  >
                    {currentProgramme.programme_name ||
                      t("workspace:programme")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <button
                    id="legacy-pending-issuance-breadcrumb-batch"
                    onClick={handleBatchClick}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleBatchClick();
                      }
                    }}
                    tabIndex={0}
                    title={currentBatch.batch_name || t("workspace:batch")}
                  >
                    {currentBatch.batch_name || t("workspace:batch")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />

                  <span
                    id="legacy-pending-issuance-breadcrumb-certificate"
                    className="text-gray-800 font-medium breadcrumb-item"
                    title={certificateName}
                  >
                    {certificateName ? certificateName : t("workspace:loading")}
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto p-4">
        <>
          <div className="max-w-5xl mx-auto">
            {/* Stat Boxes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("workspace:recipients_issued")}
                    </p>
                    <h3 className="text-3xl font-bold">
                      {recipientsIssuedCount}
                    </h3>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="border rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      {t("workspace:recipients_notified")}
                    </p>
                    <h3 className="text-3xl font-bold">
                      {recipientsNotifiedCount}
                    </h3>
                  </div>
                  <div className="bg-indigo-100 p-3 rounded-full">
                    <Mail className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Button Group */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 justify-center w-full max-w-[60rem] mx-auto mt-6">
              <button
                id="legacy-pending-issuance-print-certificates"
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-2 border border-gray-300 px-4 py-2 rounded-[8px] text-gray-800 font-medium ${
                  isPrintNotifyDisabled
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:border-cfPrimaryButton hover:bg-cfPrimaryButton transition-all duration-300 hover:text-white"
                }`}
                onClick={handlePdfPrint}
                disabled={isPrintNotifyDisabled}
              >
                <Printer size={18} />
                {t("workspace:print_certificates")}
              </button>

              <button
                id="legacy-pending-issuance-notify-recipients"
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-2 border border-gray-300 px-4 py-2 rounded-[8px] text-gray-800 font-medium ${
                  isPrintNotifyDisabled
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:border-cfPrimaryButton hover:bg-cfPrimaryButton transition-all duration-300 hover:text-white"
                } `}
                onClick={handleNotifyRecipients}
                disabled={isPrintNotifyDisabled}
              >
                <BellIcon size={18} className="inline-flex" aria-hidden />
                {t("workspace:notify_recipients")}
              </button>

              <button
                id="legacy-pending-issuance-issue"
                className={`min-w-max whitespace-nowrap flex items-center justify-center gap-3 border border-gray-300 px-4 py-2 rounded-[8px] font-medium transition-all duration-300 focus:outline-none
                ${
                  recipients.length === 0
                    ? "bg-cfPrimaryButton text-white"
                    : "bg-white text-gray-800"
                }
                ${
                  isIssueDisabled
                    ? "opacity-70 cursor-not-allowed"
                    : "cursor-pointer hover:!bg-cfPrimaryButton hover:!text-white hover:!border-cfPrimaryButton"
                }
              `}
                onClick={handleIssueNavigate}
                disabled={isIssueDisabled}
                aria-busy={isIssueDisabled}
              >
                <FlyingMail className="h-4 w-4" />
                {t("workspace:issue")}
              </button>
            </div>
          </div>

          <div className="my-6">
            <h2 className="text-xl font-semibold">
              {t("workspace:recipients")}
            </h2>
            <div className="my-4">
              <div className="flex justify-between items-center overflow-x-auto">
                <div className="relative flex-1 border-b border-gray-200">
                  <div
                    className="flex min-w-max"
                    role="tablist"
                    aria-label={t("workspace:recipients")}
                  >
                    <button
                      id="legacy-pending-issuance-tab-all"
                      role="tab"
                      aria-selected={activeRecipientTab === "all"}
                      tabIndex={activeRecipientTab === "all" ? 0 : -1}
                      onClick={() => handleTabChange("all")}
                      onKeyDown={(e) => handleRecipientTabKeyDown(e, "all")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                        activeRecipientTab === "all"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      {t("workspace:all_recipients")}
                    </button>
                    <button
                      id="legacy-pending-issuance-tab-issued"
                      role="tab"
                      aria-selected={activeRecipientTab === "issued"}
                      tabIndex={activeRecipientTab === "issued" ? 0 : -1}
                      onClick={() => handleTabChange("issued")}
                      onKeyDown={(e) => handleRecipientTabKeyDown(e, "issued")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                        activeRecipientTab === "issued"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      {t("workspace:issued")}
                    </button>
                    <button
                      id="legacy-pending-issuance-tab-failed"
                      role="tab"
                      aria-selected={activeRecipientTab === "failed"}
                      tabIndex={activeRecipientTab === "failed" ? 0 : -1}
                      onClick={() => handleTabChange("failed")}
                      onKeyDown={(e) => handleRecipientTabKeyDown(e, "failed")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                        activeRecipientTab === "failed"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      {t("workspace:failed")}
                    </button>
                    <button
                      id="legacy-pending-issuance-tab-revoked"
                      role="tab"
                      aria-selected={activeRecipientTab === "revoked"}
                      tabIndex={activeRecipientTab === "revoked" ? 0 : -1}
                      onClick={() => handleTabChange("revoked")}
                      onKeyDown={(e) => handleRecipientTabKeyDown(e, "revoked")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                        activeRecipientTab === "revoked"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      {t("workspace:revoked")}
                    </button>
                    <button
                      id="legacy-pending-issuance-tab-printed"
                      role="tab"
                      aria-selected={activeRecipientTab === "printed"}
                      tabIndex={activeRecipientTab === "printed" ? 0 : -1}
                      onClick={() => handleTabChange("printed")}
                      onKeyDown={(e) => handleRecipientTabKeyDown(e, "printed")}
                      className={`px-4 py-2 font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                        activeRecipientTab === "printed"
                          ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
                      }`}
                    >
                      {t("workspace:printed")}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4 pt-4">
                <form
                  onSubmit={(e) => e.preventDefault()}
                  className={`bg-cfSecondary p-2 rounded-lg w-full md:w-[25rem] self-center md:self-end md:m-0 border border-gray-400`}
                >
                  <input
                    type="text"
                    name="search"
                    id="legacy-pending-issuance-input-search"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    placeholder={t("workspace:search_recipients")}
                    className="outline-none border-none p-0 placeholder:text-sm w-full"
                    style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  />
                </form>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedRecipientIds.length > 0 && (
                    <>
                      {isVerificationEnabled &&
                        (verifiableSelected.length > 0 ||
                          isAllSelectedViaHeader) && (
                          <button
                            onClick={handleVerify}
                            disabled={isVerifying}
                            className={`flex items-center gap-2 rounded-[8px] px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                              isVerifying
                                ? "cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500"
                                : "cursor-pointer border border-cfPrimaryButton bg-cfPrimaryButton text-white hover:bg-cfPrimaryDark hover:border-cfPrimaryDark"
                            }`}
                          >
                            {isVerifying ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ShieldCheck className="h-4 w-4" />
                            )}
                            {isVerifying
                              ? t("workspace:verifying")
                              : hasReverifySelection || isAllSelectedViaHeader
                                ? t("workspace:re_verify")
                                : t("workspace:verify")}
                          </button>
                        )}
                      <ToolTip
                        component={
                          <button
                            id="legacy-pending-issuance-delete-recipients"
                            onClick={handleDeleteRecipients}
                            disabled={isDeleteLoading || isDeletionDisabled}
                            className={`flex items-center gap-2 rounded-[8px] px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                              isDeleteLoading || isDeletionDisabled
                                ? "cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500"
                                : "cursor-pointer border border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700"
                            }`}
                          >
                            <Trash2 className="h-4 w-4" />
                            {isDeleteLoading
                              ? t("workspace:deleting")
                              : t("workspace:delete")}
                          </button>
                        }
                        message={
                          isDeletionDisabled
                            ? `${t("workspace:deletion_allowed_only_when_status_is")} "${t("workspace:issued")}" ${t("workspace:or")} "${t("workspace:failed")}"`
                            : ""
                        }
                        tooltipPosition="bottom-10"
                        bgColor="#E0EAFF"
                        textColor="#6B7280"
                        tooltipWidth="w-64"
                      />
                      {showRevokeButton && (
                        <ToolTip
                          component={
                            <button
                              id="legacy-pending-issuance-revoke-recipients"
                              onClick={handleRevokeRecipient}
                              disabled={isRevocationDisabled}
                              className={`flex items-center gap-2 rounded-[8px] px-4 py-2.5 text-sm font-medium transition-colors duration-300 ${
                                isRevocationDisabled
                                  ? "cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500"
                                  : "cursor-pointer border border-red-500 text-red-500 hover:bg-red-50 hover:text-red-700"
                              }`}
                            >
                              <Ban className="h-4 w-4" />
                              {t("workspace:revoke")}
                            </button>
                          }
                          message={
                            isRevocationDisabled
                              ? t("workspace:revoke_credential_tooltip")
                              : ""
                          }
                          tooltipPosition="bottom-10"
                          bgColor="#E0EAFF"
                          textColor="#6B7280"
                          tooltipWidth="w-64"
                        />
                      )}
                    </>
                  )}

                  <button
                    id="legacy-pending-issuance-refresh"
                    onClick={handleManualRefresh}
                    className={`flex items-center gap-2 px-3 py-2 rounded-[8px] border border-gray-300 hover:bg-gray-100 ${
                      isManualRefresh ? "cursor-wait" : "cursor-pointer"
                    }`}
                    disabled={isManualRefresh}
                  >
                    <RefreshCWIcon
                      size={16}
                      className={`inline-flex ${
                        isManualRefresh || isPolling ? "animate-spin" : ""
                      }`}
                      aria-hidden
                    />
                    <span>{t("workspace:refresh")}</span>
                  </button>
                </div>
              </div>
            </div>

            {isRecipientsLoading ? (
              <div className="col-span-full w-full flex justify-center items-center py-4">
                <CfLoader />
              </div>
            ) : (
              <>
                {/* Table Rendering */}
                <div className="rounded-lg min-w-full overflow-auto border border-gray-300">
                  <table className="w-full">
                    <thead className="bg-cfTableHeaderBackgroundColor h-[3.563rem] text-center">
                      <tr>
                        {recipients.length > 0 && (
                          <th className="px-4 py-3">
                            <input
                              id="legacy-pending-issuance-select-all"
                              type="checkbox"
                              checked={isAllCurrentPageSelected}
                              onChange={(e) =>
                                handleSelectAllRecipients(e.target.checked)
                              }
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                        )}

                        {recipients.length > 0 &&
                          Object.keys(recipients[0])
                            .filter(
                              (key) =>
                                !getRecipientTableHiddenKeys(
                                  showQaVerificationColumn,
                                ).includes(key),
                            )
                            .map((key) => (
                              <th
                                key={key}
                                className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                              >
                                {formatHeaderText(key)}
                              </th>
                            ))}
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("workspace:action")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 text-center">
                      {recipients.length > 0 ? (
                        recipients.map((recipient) => (
                          <tr
                            key={recipient.credential_id}
                            id={`legacy-pending-issuance-row-${recipient.credential_id}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-4 whitespace-nowrap">
                              <input
                                id={`legacy-pending-issuance-checkbox-${recipient.credential_id}`}
                                type="checkbox"
                                checked={selectedRecipientIds.includes(
                                  recipient.credential_id,
                                )}
                                onChange={(e) =>
                                  handleSelectRecipient(
                                    recipient.credential_id,
                                    e.target.checked,
                                  )
                                }
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            {Object.entries(recipient)
                              .filter(
                                ([key]) =>
                                  !getRecipientTableHiddenKeys(
                                    showQaVerificationColumn,
                                  ).includes(key),
                              )
                              .map(([key, value]) => {
                                const cellId = `legacy-pending-issuance-row-${recipient.credential_id}-${key}`;
                                if (key === "status") {
                                  return (
                                    <td
                                      key={key}
                                      id={cellId}
                                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                                    >
                                      {value === "Notified" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <BellRing size={16} color="green" />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            {t("workspace:notified")}
                                          </div>
                                        </div>
                                      ) : value === "Issued" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={CorrectIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            {t("workspace:issued")}
                                          </div>
                                        </div>
                                      ) : value === "Printed" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <PrinterCheck
                                            size={16}
                                            color="green"
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            {t("workspace:printed")}
                                          </div>
                                        </div>
                                      ) : value === "Revoked" ? (
                                        <div className="flex items-center justify-center gap-2 bg-red-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusFailedBG text-xs">
                                            {t("workspace:revoked")}
                                          </div>
                                        </div>
                                      ) : value === "Failed" ? (
                                        <div className="flex items-center justify-center gap-2 bg-red-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusFailedBG text-xs">
                                            {t("workspace:failed")}
                                          </div>
                                        </div>
                                      ) : value === "Printed_and_Notified" ? (
                                        <div className="flex items-center justify-center gap-2 bg-green-200 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={CorrectIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-cfIssuedCredentialTemplateCardStatusDoneBG text-xs">
                                            {t("workspace:print_and_notified")}
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="flex items-center justify-center gap-2 bg-amber-100 px-2 py-1 rounded-xl mx-auto w-fit">
                                          <img
                                            src={PendingIcon}
                                            className="h-4 w-4"
                                            alt=""
                                          />
                                          <div className="font-medium text-amber-600 text-xs">
                                            {t("workspace:pending")}
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  );
                                }

                                if (key === "qa_status") {
                                  const isRowQaPolling = isQaPollingInProgress(
                                    recipient.qa_status,
                                  );
                                  const isRowVerified = isQaVerified(value);
                                  const isRowVerifying =
                                    Boolean(
                                      verifyingRowMap[recipient.credential_id],
                                    ) || isRowQaPolling;

                                  return (
                                    <td
                                      key={key}
                                      id={cellId}
                                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                                    >
                                      <QaTestedBadge
                                        qaTested={value}
                                        onClick={
                                          !isRowVerified && !isRowQaPolling
                                            ? () =>
                                                handleRowVerify(
                                                  recipient.credential_id,
                                                )
                                            : undefined
                                        }
                                        isLoading={isRowVerifying}
                                      />
                                    </td>
                                  );
                                }

                                return (
                                  <td
                                    key={key}
                                    id={cellId}
                                    className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                                  >
                                    {value || "N/A"}
                                  </td>
                                );
                              })}
                            <td
                              className="px-4 py-4 whitespace-nowrap text-sm text-gray-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center rounded-xl mx-auto w-fit">
                                <button
                                  id={`legacy-pending-issuance-preview-${recipient.credential_id}`}
                                  onClick={(e) =>
                                    handlePreview(
                                      e,
                                      recipient.credential_digital_preview,
                                      recipient.credential_print_preview,
                                      recipient,
                                    )
                                  }
                                  disabled={recipient.status === "Failed"}
                                  className={`flex justify-center items-center gap-1 px-3 py-1.5 text-sm font-medium ${
                                    recipient.status === "Failed"
                                      ? "pointer-events-none cursor-not-allowed opacity-50"
                                      : "text-cfPrimaryButton hover:text-cfPrimaryDark"
                                  } focus:outline-none`}
                                >
                                  <EyeIcon
                                    size={16}
                                    className="inline-flex"
                                    aria-hidden
                                  />
                                  <span>{t("workspace:preview")}</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr id="legacy-pending-issuance-no-results-row">
                          <td
                            id="legacy-pending-issuance-no-results"
                            colSpan={
                              recipients.length > 0
                                ? Object.keys(recipients[0]).length + 1
                                : 1
                            }
                            className="px-4 py-4 text-md font-semibold text-gray-500 text-center"
                          >
                            {t("workspace:no_result_found")}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 w-full">
                  <button
                    id="legacy-pending-issuance-pagination-previous"
                    onClick={() => {
                      setIsRecipientsLoading(true);
                      setRecipientsOffset(
                        Math.max(0, recipientsOffset - limit),
                      );
                    }}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!recipientsPrevious}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                      </svg>
                      <span>{t("workspace:previous")}</span>
                    </span>
                  </button>

                  <button
                    id="legacy-pending-issuance-pagination-next"
                    onClick={() => {
                      setIsRecipientsLoading(true);
                      setRecipientsOffset(recipientsOffset + limit);
                    }}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!recipientsNext}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>{t("workspace:next")}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-between items-center my-4">
            <div className="flex-1">
              {templateType === "Legacy" && (
                <h2 className="text-xl font-semibold">
                  {t("workspace:pending_issuance")}
                </h2>
              )}
            </div>
          </div>
          {templateType === "Legacy" &&
            (isLoading ? (
              <div className="col-span-full w-full flex justify-center items-center py-4">
                <CfLoader />
              </div>
            ) : (
              <>
                {Array.isArray(transactions) && transactions.length > 0 ? (
                  <div className="rounded-lg min-w-full overflow-auto border border-gray-300">
                    <table className="w-full">
                      <thead className="bg-cfTableHeaderBackgroundColor h-[3.563rem] text-center">
                        <tr>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            {t("workspace:transaction_name")}
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            {t("workspace:status")}
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            {t("workspace:progress")}
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7] whitespace-nowrap">
                            {t("workspace:created_at")}
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7] whitespace-nowrap">
                            {t("workspace:updated_at")}
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            CTA
                          </th>
                          <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-[1/7]">
                            {t("workspace:action")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 h-[3.563rem] text-center">
                        {transactions.map((transaction) => (
                          <tr
                            key={transaction.legacy_transaction_id}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm font-medium text-gray-900">
                              {transaction.transaction_name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.status)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              <div className="flex justify-center items-center w-full">
                                <ProgressBar
                                  status={
                                    transaction.status === "In Progress"
                                      ? "in-progress"
                                      : transaction.issuance_status
                                            ?.transaction_status === "Failed"
                                        ? "failed"
                                        : "completed"
                                  }
                                  success={
                                    transaction.issuance_status?.issued || 0
                                  }
                                  failed={
                                    transaction.issuance_status?.failed || 0
                                  }
                                  total={
                                    transaction.issuance_status
                                      ?.total_recipients || 0
                                  }
                                  showPercentage={Math.round(
                                    ((transaction.issuance_status?.issued ||
                                      0) /
                                      (transaction.issuance_status
                                        ?.total_recipients || 1)) *
                                      100,
                                  )}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.created_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm text-gray-500">
                              {getNextStepText(transaction.updated_at)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm items-center">
                              <div className="w-[7rem] mx-auto">
                                <button
                                  disabled={
                                    rowLoadingMap[
                                      transaction.legacy_transaction_id
                                    ]
                                  }
                                  onClick={() => {
                                    if (
                                      !rowLoadingMap[
                                        transaction.legacy_transaction_id
                                      ]
                                    ) {
                                      navigate(getResumeUrl(transaction), {
                                        state: {
                                          transactionData: transaction,
                                          fromResume:
                                            transaction.status ===
                                            "ZIP Uploaded",
                                        },
                                      });
                                    }
                                  }}
                                  className={`border border-gray-500 px-4 py-2 rounded-[8px] text-black font-medium transition-colors duration-300 w-full
                                    ${
                                      rowLoadingMap[
                                        transaction.legacy_transaction_id
                                      ]
                                        ? "bg-gray-200 opacity-60 cursor-not-allowed"
                                        : "hover:bg-gray-100 hover:text-black cursor-pointer"
                                    }`}
                                  style={{ textDecoration: "none" }}
                                >
                                  {t("workspace:resume")}
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap w-[1/7] text-sm items-center">
                              <div className="w-[7rem] mx-auto">
                                <button
                                  onClick={() =>
                                    handleDeletePendingIssuance(
                                      transaction.legacy_transaction_id,
                                    )
                                  }
                                  className="flex items-center justify-center gap-2 border border-red-500 px-4 py-2 rounded-[8px] text-red-500 font-medium hover:bg-red-50 hover:text-red-700 transition-colors duration-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  {t("workspace:delete")}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="flex flex-col flex-wrap sm:mt-0 xl:mt-6 w-full bg-white min-h-screen rounded-2xl">
                    <div className="flex flex-col px-2 lg:px-0 mt-20 justify-center items-center gap-5">
                      <img
                        alt="certificate-icon"
                        src={podcastIcon}
                        height="80px"
                        width="80px"
                        className=""
                      />
                      <h1 className="text-cfHeadingText font-semibold text-center">
                        {t("workspace:no_pending_issuance")}
                      </h1>
                      <h3 className="text-xs font-bold text-center lg:text-md text-gray-500">
                        {t("workspace:no_pending_issuance_description")}
                      </h3>
                      <button
                        id="legacy-pending-issuance-empty-issue"
                        className={`rounded-[8px] items-center bg-cfPrimaryButton text-white font-medium lg:font-semibold px-5 py-2 text-sm lg:text-md ${
                          isIssueDisabled
                            ? "opacity-70 cursor-not-allowed"
                            : "hover:bg-cfPrimaryDark"
                        }`}
                        onClick={handleIssueNavigate}
                        disabled={isIssueDisabled}
                        aria-busy={isIssueDisabled}
                      >
                        {t("workspace:issue")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between mt-6 w-full">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!previous}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                      </svg>
                      <span>{t("workspace:previous")}</span>
                    </span>
                  </button>

                  <button
                    onClick={() => setOffset(offset + limit)}
                    className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                    disabled={!next}
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>{t("workspace:next")}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                      </svg>
                    </span>
                  </button>
                </div>
              </>
            ))}
        </>
      </div>
      <CertificatePreviewModal
        isOpen={isCertificateModalOpen}
        onClose={() => setIsCertificateModalOpen(false)}
        previewUrl={selectedPreviewUrl}
        printPreviewUrl={selectedPrintUrl}
        isLoading={isLoadingPreview}
        credential_id={selectedRecipient?.credential_id}
      />
      <VerifyCredentialsModal
        isOpen={isVerificationEnabled && isVerifyModalOpen}
        onClose={() => setIsVerifyModalOpen(false)}
        onConfirm={handleConfirmVerify}
        recipients={verifiableSelected}
        isLoading={isVerifying}
      />
      <DraftsRevokeModal
        isOpen={isVerificationEnabled && isVerifyAllConfirmOpen}
        onClose={() => setIsVerifyAllConfirmOpen(false)}
        onConfirm={handleConfirmVerifyAll}
        title={t("workspace:verify")}
        description={t("workspace:verify_all_confirm")}
        confirmButtonText={
          isVerifying ? t("workspace:verifying") : t("workspace:verify")
        }
        cancelButtonText={t("workspace:cancel")}
        confirmButtonColor="bg-cfPrimaryButton hover:bg-cfPrimaryDark"
      />
      <DraftsRevokeModal
        isOpen={isDeletePendingIssuanceModalOpen}
        onClose={() => {
          setIsDeletePendingIssuanceModalOpen(false);
          setPendingIssuanceToDelete(null);
        }}
        onConfirm={handleConfirmDeletePendingIssuance}
        title={t("workspace:revoke_pending_issuance")}
        description={
          <div className="flex flex-col">
            {t("workspace:revoke_pending_issuance_description")}
            <span className="font-semibold text-black mt-2">
              {t("workspace:revoke_are_you_sure")}
            </span>
          </div>
        }
        confirmButtonText={
          isDeleteLoading ? t("workspace:deleting") : t("workspace:delete")
        }
        cancelButtonText={t("workspace:cancel")}
        confirmButtonColor="bg-red-600 hover:bg-red-800"
      />
      <MultiSectionModal
        isOpen={isDeleteRecipientsModalOpen}
        onClose={() => setIsDeleteRecipientsModalOpen(false)}
        onConfirm={handleConfirmDeleteRecipients}
        title={`${t("workspace:delete_certificates")}${
          selectedRecipientIds.length > 1 ? "s" : ""
        }?`}
        description={
          <div className="flex flex-col">
            {(() => {
              const selectedRecipients = recipients.filter((r) =>
                selectedRecipientIds.includes(r.credential_id),
              );
              const selectionMismatch =
                selectedRecipients.length !== selectedRecipientIds.length;
              const allFailed =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Failed");
              const allIssued =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Issued");
              const allRevoked =
                !selectionMismatch &&
                selectedRecipients.every((r) => r.status === "Revoked");

              let statusText = "";
              if (selectionMismatch) {
                statusText = `${t("workspace:selected_certificate")}${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allFailed) {
                statusText = `${t("workspace:failed_certificate")}${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allIssued) {
                statusText = `${t("workspace:issued_certificate")}${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else if (allRevoked) {
                statusText = `${t("workspace:revoked_certificate")}${
                  selectedRecipientIds.length > 1 ? "s" : ""
                }`;
              } else {
                // Mixed statuses
                const statusCounts = selectedRecipients.reduce((acc, r) => {
                  acc[r.status] = (acc[r.status] || 0) + 1;
                  return acc;
                }, {});

                const statusList = Object.entries(statusCounts)
                  .map(([status, count]) => `${count} ${status.toLowerCase()}`)
                  .join(", ");

                statusText = `${t("workspace:certificate")}${
                  selectedRecipientIds.length > 1 ? "s" : ""
                } (${statusList})`;
              }

              return (
                <>
                  {t("workspace:multi_section_description_1")}{" "}
                  {selectedRecipientIds.length} {statusText}.{" "}
                  {t("workspace:multi_section_description_2")}{" "}
                  <span className="font-semibold text-black mt-2">
                    {t("workspace:revoke_are_you_sure")}
                  </span>
                </>
              );
            })()}
          </div>
        }
        confirmButtonText={
          isDeleteLoading ? t("workspace:deleting") : t("workspace:delete")
        }
        cancelButtonText={t("workspace:cancel")}
        confirmButtonColor="bg-red-600 hover:bg-red-800"
        selectedRecipientIds={selectedRecipientIds}
      />
      <CredentialRevokeModal
        isModalOpen={recipientToRevoke}
        toggleModal={() => {
          setRecipientToRevoke(null);
        }}
        GET_ALL_TRANSACTIONS_LIST={async () => {
          await fetchRecipientStats();
          await fetchRecipients();
          clearRecipientSelection();
        }}
      />
    </div>
  );
}
